import { Clock, CheckCircle, XCircle, ArrowDownCircle, ArrowUpCircle, Gift, TrendingUp } from 'lucide-react';
import { Badge } from '../ui/badge';
import { CurrencyDisplay } from './CurrencyDisplay';
import { cn } from '../../lib/utils';

/**
 * Get status icon based on transaction status
 */
const getStatusIcon = (status) => {
  switch (status) {
    case 'approved':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'rejected':
      return <XCircle className="w-4 h-4 text-red-500" />;
    default:
      return <Clock className="w-4 h-4 text-amber-500" />;
  }
};

/**
 * Get status badge based on transaction status
 */
const getStatusBadge = (status) => {
  switch (status) {
    case 'approved':
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Approved</Badge>;
    case 'rejected':
      return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Rejected</Badge>;
    default:
      return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>;
  }
};

/**
 * Get transaction type icon
 */
const getTypeIcon = (type) => {
  switch (type) {
    case 'deposit':
      return <ArrowDownCircle className="w-5 h-5 text-green-600" />;
    case 'withdrawal':
      return <ArrowUpCircle className="w-5 h-5 text-red-600" />;
    case 'interest':
      return <TrendingUp className="w-5 h-5 text-blue-600" />;
    case 'bonus':
      return <Gift className="w-5 h-5 text-purple-600" />;
    default:
      return <ArrowDownCircle className="w-5 h-5 text-slate-400" />;
  }
};

/**
 * Get background color based on transaction type
 */
const getTypeBgColor = (type) => {
  switch (type) {
    case 'deposit':
      return 'bg-green-100';
    case 'withdrawal':
      return 'bg-red-100';
    case 'interest':
      return 'bg-blue-100';
    case 'bonus':
      return 'bg-purple-100';
    default:
      return 'bg-slate-100';
  }
};

/**
 * Format transaction type for display
 */
const formatType = (type) => {
  return type.charAt(0).toUpperCase() + type.slice(1);
};

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Single Transaction Item
 */
export const TransactionItem = ({ 
  transaction, 
  showUsername = false,
  showStatus = true,
  variant = "default",
  className 
}) => {
  const { type, amount, description, status, created_at, username } = transaction;
  
  const isCredit = type === 'deposit' || type === 'interest' || type === 'bonus';
  
  return (
    <div 
      className={cn(
        "flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-xl transition-all gap-2 sm:gap-4",
        variant === "default" ? "bg-slate-50 hover:bg-slate-100" : "bg-white border border-slate-200",
        className
      )}
      data-testid={`transaction-item-${transaction.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", getTypeBgColor(type))}>
          {getTypeIcon(type)}
        </div>
        <div className="min-w-0">
          <p className="font-medium text-slate-800 truncate">
            {showUsername && username ? `${username} - ` : ''}{formatType(type)}
          </p>
          <p className="text-sm text-slate-500 truncate">
            {description || formatDate(created_at)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:gap-4 self-end sm:self-auto shrink-0">
        <CurrencyDisplay 
          amount={amount} 
          showSign={true}
          type={isCredit ? 'deposit' : 'withdrawal'}
          size="sm"
        />
        {showStatus && getStatusBadge(status)}
      </div>
    </div>
  );
};

/**
 * Transaction List Component
 * Reusable list of transactions with various display options
 */
export const TransactionList = ({ 
  transactions = [], 
  showUsername = false,
  showStatus = true,
  variant = "default",
  emptyMessage = "No transactions yet",
  emptyIcon: EmptyIcon = Clock,
  maxItems,
  className 
}) => {
  const displayTransactions = maxItems ? transactions.slice(0, maxItems) : transactions;

  if (transactions.length === 0) {
    return (
      <div className="text-center py-12" data-testid="empty-transactions">
        <EmptyIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)} data-testid="transaction-list">
      {displayTransactions.map((tx) => (
        <TransactionItem 
          key={tx.id}
          transaction={tx}
          showUsername={showUsername}
          showStatus={showStatus}
          variant={variant}
        />
      ))}
      {maxItems && transactions.length > maxItems && (
        <p className="text-center text-sm text-slate-500 pt-2">
          +{transactions.length - maxItems} more transactions
        </p>
      )}
    </div>
  );
};

export default TransactionList;
